import {
  AppInsightsContext,
  Link,
  ModuleBase,
  PopoverMenu,
  ProgressCircular,
  SystemFeaturesContext,
  Tooltip,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  useNotification,
  UserContext,
} from '@/components';
import {
  AIChatMessageModel,
  GDAIChatRequest,
  GDAIChatSource,
  LinkModel,
  ModuleBaseModel,
  ModuleBasePropsModel,
} from '@/types';
import classNames from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import StylusAIWordmark from '@/assets/stylusAIWordmark_whiteRGBRegistered.svg';
import AISearch from '@/assets/stylus-ai-search.svg';
import { gdAIchat, saveToScrapbookWithPath } from '@/api';
import Copy from '@/assets/copy-simple-light.svg';
import { useTranslations } from 'next-intl';
import { marked } from 'marked';
import { scrollIntoView } from '@/utils/content';

type AIChatExtendedMessageModel = {
  sources?: GDAIChatSource[];
  suggestedQuestions?: string[];
} & AIChatMessageModel;

type AIchatIntegrationModel = ModuleBaseModel<
  {
    disclaimer: string;
    helpText: string;
    msg: string;
  } & ModuleBasePropsModel
>;

const AIchatIntegration2 = (data: AIchatIntegrationModel) => {
  const {
    props: { disclaimer, helpText },
  } = data;

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const buttonsTopRef = useRef<HTMLDivElement>(null);
  const topElementRef = useRef<HTMLDivElement>(null);
  const appInsights = useContext(AppInsightsContext);
  const { saveToScrapbook: saveToScrapbookFeature } = useContext(SystemFeaturesContext);
  const user = useContext(UserContext);
  const { showNotification } = useNotification();
  const t = useTranslations();

  const [disabled, setDisabled] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [query, setQuery] = useState('');
  const [placeholder, setPlaceholder] = useState('Ask me a question...');
  const [last12m, setLast12m] = useState(false);
  const [discussion, setDiscussion] = useState<Array<AIChatExtendedMessageModel>>([]);

  const conversationkey = useRef<string>('');

  // Auto-resize on value change
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }
  }, [query]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setDisabled(true);
    setWaiting(true);

    appInsights.trackCustomEvent(TrackEventName.AI_CHAT, TrackEventKey.QUESTION, TrackEventType.ASKED, user, {
      query,
    });

    const chatRequest: GDAIChatRequest = {
      query: query?.replace(/["]+/g, ''),
      published_12m_filter: last12m,
      conversationkey: conversationkey.current,
      isCitationsEnable: true,
    };

    gdAIchat(chatRequest)
      .then((response) => {
        conversationkey.current = response.conversationkey;

        const userMessage = response.messages.find((msg) => msg.role === 'user');
        const assistantMessageRaw = response.messages.find((msg) => msg.role === 'assistant');

        if (assistantMessageRaw) {
          const assistantMessage = parseMarkdown(assistantMessageRaw.content, response.sources);

          setDiscussion([
            ...discussion,
            userMessage as AIChatExtendedMessageModel,
            {
              ...assistantMessageRaw,
              content: assistantMessage.html,
              sources: assistantMessage.supRefs,
            } as AIChatExtendedMessageModel,
          ]);
        }
        setPlaceholder('Ask me a follow up question...');
        setQuery('');
        setTimeout(() => {
          if (buttonsTopRef.current) {
            buttonsTopRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });
          }
        }, 100);
      })
      .catch((error) => {
        console.error('Error fetching AI chat response:', error);
      })
      .finally(() => {
        setDisabled(false);
        setWaiting(false);
      });

    if (textareaRef.current) {
      textareaRef.current.value = '';
    }
  };

  const cleanAll = () => {
    setDiscussion([]);
    setPlaceholder('Ask me a question...');
    setQuery('');

    if (topElementRef.current) {
      const style = window.getComputedStyle(document.body);
      if (window.lenis) {
        window.lenis.scrollTo(topElementRef.current, {
          offset: parseInt(style.getPropertyValue('--header-height'), 10) * -1,
        });
      } else {
        scrollIntoView(topElementRef.current, parseInt(style.getPropertyValue('--header-height'), 10) * -1);
      }
    }
  };

  const saveToScrapbook = async () => {
    if (!saveToScrapbookFeature) {
      showNotification(t('membershipLevels.$featurenotavailable'));
      return;
    }

    const result = await saveToScrapbookWithPath(
      {
        path: `/Stylus AI/stylus-ai.${Date.now()}.json`,
        contentType: 'application/x.stylus.sytlus-ai+json',
        content: JSON.stringify({
          pageTitle: 'Stylus AI',
          response: discussion.map((msg) => ({
            role: msg.role,
            content:
              msg.content +
              (msg.role === 'assistant'
                ? (disclaimer ? `\n<i>${disclaimer}</i>` : '') +
                  (msg.sources && msg.sources.length > 0 ? '\n\nReference Content:<br>' : '')
                : ''),
            references: msg.sources?.map((source) => ({
              title: source.title,
              link: source.meta_Link,
            })),
          })),
        }),
      },
      user?.userId,
    );

    if (result.succeeded) {
      showNotification(result.message || 'Saved to Stylus AI', 'savesuccess');
    } else {
      showNotification('Failed to save to scrapbook', 'error');
      console.error(result);
    }
  };

  return (
    <ModuleBase
      data={{ ...data, props: { ...data.props, backgroundColour: '#212429ff' } }}
      noPadding
      className="relative min-h-[calc(100dvh-var(--header-height))] pb-5 md:pb-16"
    >
      <div className="container flex flex-col items-center" ref={topElementRef}>
        <div className="relative w-1/5 pt-8 pb-5">
          <StylusAIWordmark className="" />
        </div>

        {discussion && (
          <div className="mb-3 flex w-full flex-col gap-4">
            {discussion.map((msg, index) => (
              <AIchatMessage key={index} data={msg} disclaimer={disclaimer} />
            ))}
          </div>
        )}

        <form onSubmit={onSubmit} className="flex w-full items-center rounded-4xl bg-[#3a3d42] p-4 lg:w-10/12">
          <textarea
            id="query"
            ref={textareaRef}
            className="min-h-6 w-full resize-none rounded-md bg-transparent font-bold text-white transition-colors outline-none placeholder:text-white/60"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSubmit(e);
              }
            }}
            rows={1}
            placeholder={placeholder}
            disabled={disabled}
            value={query}
          />
          <div className="ml-4 flex w-11 justify-end">
            {waiting ? (
              <ProgressCircular indeterminate size={28} width={4} colorClassName="stroke-black" />
            ) : (
              <button className="cursor-pointer" onClick={onSubmit} disabled={disabled}>
                <AISearch className="h-7 w-7" />
              </button>
            )}
          </div>
        </form>
        <div className="mt-12 ml-4 w-full lg:w-10/12">
          <label className="inline-flex cursor-pointer items-center">
            <input
              id="last12m"
              type="checkbox"
              checked={last12m}
              onChange={() => setLast12m(!last12m)}
              className="peer sr-only"
            />
            <div
              className={classNames(
                'bg-neutral-quaternary peer relative h-6 w-12 rounded-full bg-black',
                "after:absolute after:start-[4px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-['']",
                'peer-focus:outline-none',
                'peer-checked:after:border-buffer peer-checked:bg-brand peer-checked:bg-[#ff626a54] peer-checked:after:translate-x-full peer-checked:after:bg-[#ff6269]',
              )}
            ></div>
            <span className="ms-3 text-base font-medium text-[#676975] select-none">
              Only search content from the last 12 months
            </span>
          </label>
        </div>

        {discussion.length > 0 && (
          <div ref={buttonsTopRef} className="mt-8 flex flex-col justify-center gap-3 md:flex-row md:gap-20">
            {saveToScrapbookFeature && (
              <button
                className="btn !bg-[#ff6269] !px-6 !py-4 !font-normal tracking-[1.42857px]"
                onClick={saveToScrapbook}
              >
                Save To Scrapbook
              </button>
            )}
            <button className="btn !bg-white !px-6 !py-4 !font-normal tracking-[1.42857px]" onClick={cleanAll}>
              Start new chat
            </button>
          </div>
        )}
      </div>
      <div className="absolute bottom-3 left-3">
        <PopoverMenu
          title="?"
          className={classNames(
            'btn icon h-14 w-14 !bg-[#ff6269] [&_.arrow]:hidden',
            '[&_.content]:!max-h-fit [&_.content]:rounded-4xl [&_.content]:border-[3px] [&_.content]:border-white [&_.content]:bg-[#3a3d42]',
            '[&_.arrow-container]:hidden [&_.content-wrapper]:w-[300px]',
          )}
          titleClass="w-full text-2xl font-normal"
          offset={14}
          placement="top-start"
        >
          <div
            dangerouslySetInnerHTML={{ __html: helpText || 'Help text is missing; edit this page in the CMS.' }}
            className="body-2 p-8 !text-[13px] font-normal text-white [&_li]:p-2"
          />
        </PopoverMenu>
      </div>
    </ModuleBase>
  );
};

export default AIchatIntegration2;

const AIchatMessage = ({ data, disclaimer }: { data: AIChatExtendedMessageModel; disclaimer: string }) => {
  const { role, content, sources } = data;

  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const [copied, setCopied] = useState(false);

  const html = content;
  const supRefs = sources;

  const copyToClipboard = () => {
    let copyTxt = html.replace(/<[^>]*>/g, '').trim();
    copyTxt += `\n\n${disclaimer.replace(/<[^>]*>/g, '').trim()}`;

    let formattedHtmlContent = '';
    formattedHtmlContent += '<html>';
    formattedHtmlContent += '<body>';
    formattedHtmlContent += html;
    formattedHtmlContent += `<br><i>${disclaimer}</i>`;
    if (supRefs && supRefs.length > 0) {
      formattedHtmlContent +=
        '<br>Reference Content:<br>' +
        // .filter((resp: any) => resp.role === 'assistant')
        supRefs.map((resp) => `<a href="${resp.meta_Link}">${resp.title}</a>`).join('<br>') +
        '<br><br>';

      copyTxt +=
        '\n\nReference Content:\n\n' + supRefs.map((resp) => `${resp.title} ${resp.meta_Link}`).join('\n') + '\n\n';
    }
    formattedHtmlContent += '</body>';
    formattedHtmlContent += '</html>';

    //copyTxt = copyTxt.replaceAll(']</sup>', '').replaceAll('<sup>[', '');

    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([formattedHtmlContent], { type: 'text/html' }),
        'text/plain': new Blob([copyTxt], { type: 'text/plain' }),
      }),
    ]);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  const trackEvent = (ref: GDAIChatSource) => {
    appInsights.trackCustomEvent(TrackEventName.AI_PAGE, TrackEventKey.REFERENCE, TrackEventType.CLICK, user, {
      ref_link: ref.meta_Link,
      ref_title: ref.title,
    });
  };

  return (
    <div
      className={classNames(
        'w-fit rounded-3xl p-4',
        role === 'user' ? 'ml-18 self-end bg-[#bdbdbd]' : 'mr-18 bg-[#3a3d42] text-white',
      )}
    >
      <div
        className="richText [&_a]:!text-[#6ea8ff] [&_h2]:!my-3 [&_h3]:!my-3 [&_p]:!my-3"
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {role === 'assistant' && (
        <>
          <div className="mt-4 font-normal text-[#676975]" dangerouslySetInnerHTML={{ __html: disclaimer }}></div>

          {supRefs && supRefs.length > 0 && (
            <>
              <div className="py-4 font-bold">Reference Content:</div>
              {supRefs.map((ref, index) => (
                <div key={index} className="border-t border-[#676975] py-4">
                  <Link
                    link={
                      {
                        href: ref.meta_Link,
                        text: ref.title,
                        target: '_blank',
                      } as LinkModel
                    }
                    className="font-bold"
                    onClick={() => trackEvent(ref)}
                  />
                </div>
              ))}
            </>
          )}
          <div className="flex items-center gap-4">
            <Tooltip placement="bottom" content="Copy to Clipboard" className="w-fit">
              <button
                className="cursor-pointer rounded-full p-1 opacity-70 transition-opacity hover:opacity-100"
                onClick={copyToClipboard}
              >
                <Copy className="h-[34px] w-[34px] fill-white" />
              </button>
            </Tooltip>
            <div className={classNames('transition-opacity', copied ? 'opacity-100' : 'opacity-0')}>Copied...</div>
          </div>
        </>
      )}
    </div>
  );
};

type SupRef = {
  raw: string;
  href?: string;
  title?: string;
  ids: number[];
};

type ParseMarkdownResult = {
  html: string;
  supRefs: GDAIChatSource[];
};

export function parseMarkdown(md: string, sources?: GDAIChatSource[]): ParseMarkdownResult {
  let supRefs: SupRef[] = [];

  const renderer = new marked.Renderer();

  renderer.link = ({ href, title, tokens }) => {
    const text = marked.Parser.parseInline(tokens);
    const titleAttr = title ? ` title="${title}"` : '';

    return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
  };

  marked.use({
    renderer,
    walkTokens(token) {
      if (token.type === 'link') {
        const raw = token.raw ?? '';
        const matches = [...raw.matchAll(/\[(\d+)\]/g)];
        if (matches.length > 0) {
          const existingRef = supRefs.find((r) => r.href === token.href);
          if (existingRef) {
            existingRef.ids.push(...matches.map((m) => Number(m[1])));
          } else {
            const source = sources?.find((source) => source.meta_Link === token.href);
            supRefs.push({
              raw,
              href: token.href,
              title: source?.title,
              ids: matches.map((m) => Number(m[1])),
            });
          }
        }
      }
    },
  });

  const html = marked.parse(md) as string;

  supRefs = supRefs.map((ref) => ({
    ...ref,
    title: `${[...new Set(ref.ids)].map((id) => `[${id}]`).join(' ')} ${ref.title}`,
    ids: [...new Set(ref.ids)],
  }));

  return {
    html,
    supRefs: supRefs.map((ref) => ({
      id: ref.ids[0],
      title: ref.title || '',
      meta_Link: ref.href || '',
      content: '',
    })),
  };
}
